import { DateTime } from "luxon";
import { v4 as uuid } from "uuid";
import findKey from "lodash/findKey";
import { ConfidenceLevel, KeyResultBinaryChoice, KeyResultType } from "@teaming/common-enums";

import {
	validateGoalForm,
	validateKeyResultForm
} from "./validation";

import {
	SHORT_DATE_FORMAT
} from "./mapping";

const allTouched = touches => !findKey( touches, isTouched => !isTouched );
const anyTouched = touches => !!findKey( touches, isTouched => isTouched );
const allEmpty = errors => !findKey( errors, errorMessage => !!errorMessage );

export const emptyValueFields = {
	[ KeyResultType.BINARY ]: () => ( {
		startValue: "", // FUTURE IMPLEMENTATION
		currentValue: KeyResultBinaryChoice.INCOMPLETE,
		targetValue: KeyResultBinaryChoice.COMPLETE,
	} ),
	[ KeyResultType.CURRENCY ]: () => ( {
		startValue: "", // FUTURE IMPLEMENTATION
		currentValue: "0.00",
		targetValue: "0.00",
	} ),
	[ KeyResultType.NUMERIC ]: () => ( {
		startValue: "", // FUTURE IMPLEMENTATION
		currentValue: "0",
		targetValue: "100",
	} ),
	[ KeyResultType.PERCENTAGE ]: () => ( {
		startValue: "", // FUTURE IMPLEMENTATION
		currentValue: "0",
		targetValue: "100",
	} ),
};

const computedFormProperties = () => ( {
	revision: 0,
	$untouchedForm: null,
	touches: {},
	errors: {},
	allFieldsTouched: false,
	allFieldsValid: true,
	isTouched: false,
	capabilities: {},
} );

const emptyKeyResultForm = () => ( {
	formId: uuid(),
	keyResultId: "",
	goalId: "",
	teamId: "",
	sortOrder: 0,
	name: "",
	confidenceLevel: ConfidenceLevel.HIGH,
	type: KeyResultType.NUMERIC,
	...emptyValueFields[ KeyResultType.NUMERIC ](),
	creatorId: "",
	creatorName: "",
	ownerId: "",
	ownerName: "",
	ownerAvatar: "",
	ownerEmail: "",
	createdDate: "",
	lastUpdatedDate: "",
	pacing: 0,
	progress: 0,
	comment: "",

	// derived
	isNew: true,
	parentGoalName: "",
	startDate: "",
	endDate: "",

	// computed
	...computedFormProperties(),
} );

const emptyGoalForm = () => ( {
	formId: uuid(),
	goalId: "",
	title: "",
	assignedTeamId: "",
	assignedTeamName: "",
	confidenceLevel: ConfidenceLevel.HIGH,
	startDate: DateTime.now().toFormat( SHORT_DATE_FORMAT ),
	// eslint-disable-next-line no-magic-numbers
	endDate: DateTime.now().plus( { days: 90, } ).toFormat( SHORT_DATE_FORMAT ),
	lastUpdatedDate: "",
	progress: 0,
	pacing: 0,
	comment: "",

	// derived
	isNew: true,
	keyResultCount: 0,

	// computed
	...computedFormProperties(),
} );

const touchGoalForm = ( form, untouchedForm, coerce = false ) => {
	return {
		title: coerce || form.title !== untouchedForm.title,
		assignedTeamId: coerce || form.assignedTeamId !== untouchedForm.assignedTeamId,
		confidenceLevel: coerce || form.confidenceLevel !== untouchedForm.confidenceLevel,
		startDate: coerce || form.startDate !== untouchedForm.startDate,
		endDate: coerce || form.endDate !== untouchedForm.endDate,
		keyResultCount: coerce || form.keyResultCount !== untouchedForm.keyResultCount,
		comment: coerce || form.comment !== untouchedForm.comment,
	};
};

const touchKeyResultForm = ( form, untouchedForm, coerce = false ) => {
	return {
		name: coerce || form.name !== untouchedForm.name,
		confidenceLevel: coerce || form.confidenceLevel !== untouchedForm.confidenceLevel,
		type: coerce || form.type !== untouchedForm.type,
		startValue: coerce || form.startValue !== untouchedForm.startValue,
		currentValue: coerce || form.currentValue !== untouchedForm.currentValue,
		targetValue: coerce || form.targetValue !== untouchedForm.targetValue,
		ownerId: coerce || form.ownerId !== untouchedForm.ownerId,
	};
};

const computeGoalCapabilities = form => {
	const { goalId, isArchived, } = form;
	return {
		archive: !!goalId && !isArchived,
		unarchive: !!goalId && isArchived,
		remove: !!goalId && !isArchived,
		edit: !!goalId && !isArchived,
		addKeyResult: !!goalId && !isArchived,
		editKeyResult: !!goalId && !isArchived,
	};
};

const computeKeyResultCapabilities = form => {
	const { keyResultId, } = form;
	return {
		remove: !!keyResultId,
		// computed in store based on goal capabilities
		edit: true,
	};
};

const GOAL_FORM_OPTIONS = { touchAll: false, };

// TODO: use formFactory
export const newGoalForm = ( priorForm = {}, i18n, options = {} ) => {
	const mergedOptions = {
		...GOAL_FORM_OPTIONS,
		...options,
	};

	const form = {
		...emptyGoalForm(),
		...priorForm,
	};

	if ( !form.revision ) {
		form.$untouchedForm = {
			...form,
		};
	}

	form.revision = ( form.revision || 0 ) + 1;
	form.isNew = !form.goalId;
	form.touches = touchGoalForm( form, form.$untouchedForm, mergedOptions.touchAll );
	form.errors = validateGoalForm( form, form.touches, i18n );
	form.allFieldsTouched = allTouched( form.touches );
	form.allFieldsValid = allEmpty( form.errors );
	form.isTouched = anyTouched( form.touches );
	form.capabilities = computeGoalCapabilities( form );
	return form;
};

const KEY_RESULT_FORM_OPTIONS = { touchAll: false, resetValues: false, };

// TODO: use formFactory
export const newKeyResultForm = ( priorForm = {}, i18n, options = {} ) => {
	const mergedOptions = {
		...KEY_RESULT_FORM_OPTIONS,
		...options,
	};

	let form = {
		...emptyKeyResultForm(),
		...priorForm,
	};

	if ( options.resetValues ) {
		form = {
			...form,
			...emptyValueFields[ form.type ](),
		};
	}

	if ( !form.revision ) {
		form.$untouchedForm = {
			...form,
		};
	}

	form.revision = ( form.revision || 0 ) + 1;
	form.isNew = !form.keyResultId;
	form.touches = touchKeyResultForm( form, form.$untouchedForm, mergedOptions.touchAll );
	form.errors = validateKeyResultForm( form, form.touches, i18n );
	form.allFieldsTouched = allTouched( form.touches );
	form.allFieldsValid = allEmpty( form.errors );
	form.isTouched = anyTouched( form.touches );
	form.capabilities = computeKeyResultCapabilities( form );
	return form;
};
